"use client"

import * as React from "react"
import * as SwitchPrimitives from "@radix-ui/react-switch"

import { cn } from "@/lib/utils"

export type SwitchProps = React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, disabled, checked, ...props }, ref) => {
  const control = (
  <SwitchPrimitives.Root
    className={cn(
      // The unchecked track previously used `bg-input`/`bg-background` (generic
      // shadcn tokens, never tuned for this app's `cs-*` palette), then
      // `cs-surface-hover` — still too close to the card in both themes. Now
      // `cs-text-disabled` for a genuine mid-gray unchecked track, and — the
      // real bug behind BOTH states rendering invisible, not just unchecked —
      // driven off the `checked` PROP rather than `data-[state=checked]:`.
      // Any caller that passes `hint` (nearly everyone, per FAB-154) wraps
      // this Root as the direct child of `Hint`'s `TooltipTrigger asChild`.
      // Radix's Slot merges plain DOM attributes from the outer primitive
      // onto the child element it clones onto — so the shared `data-state`
      // attribute name became Tooltip's own open/closed vocabulary
      // ("closed"/"delayed-open"/"instant-open"), permanently clobbering
      // Switch's "checked"/"unchecked" value on that DOM node. `data-state`
      // still worked on the Thumb below (a separate child element, driven by
      // Radix Switch's own internal context, never touched by the outer
      // Slot's merge) — which is why the thumb still slid to the right while
      // the track stayed transparent regardless of state.
      "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
      checked ? "bg-primary" : "bg-cs-text-disabled",
      className
    )}
    checked={checked}
    {...props}
    disabled={disabled}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={cn(
        // Always white (not a theme token) so the knob stays visibly lighter
        // than the track in both themes — `bg-background` matched the page's
        // near-black in dark mode, which is what made the thumb disappear.
        "pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
      )}
    />
  </SwitchPrimitives.Root>
  );
  return control;
})
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
